import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { consumeLocalSyncChanges, syncBrowser, syncOwner } from '../../sync/browser-sync';
import { readLastSuccessfulSync, SYNC_STATUS_EVENT, SYNC_STATUS_KEY } from '../../sync/sync-status';
import type { SafeSyncResult } from '../../sync/safe-sync';
import { createAutoSyncScheduler } from './auto-sync-scheduler';

const REVIEW_KEY = 'yumnote.sync.needsReview';
function initialCount(): number {
  try { const value=Number(sessionStorage.getItem(REVIEW_KEY) ?? 0); return Number.isSafeInteger(value) && value>0 ? value : 0; } catch { return 0; }
}
export function useAutomaticAccountSync(userId: string | null, safeScreen: boolean) {
  const safe = useRef(safeScreen); safe.current=safeScreen;
  const [running,setRunning]=useState(false);
  const [checking,setChecking]=useState(false);
  const [waiting,setWaiting]=useState(false);
  const [pending,setPending]=useState(false);
  const [online,setOnline]=useState(()=>navigator.onLine);
  const [lastSuccessfulAt,setLastSuccessfulAt]=useState(()=>readLastSuccessfulSync(userId));
  const [conflictCount,setCount]=useState(initialCount);
  const [error,setError]=useState('');
  function setConflictCount(value:number) {
    const count=Math.max(0,value);setCount(count);
    try { if(count) sessionStorage.setItem(REVIEW_KEY,String(count)); else sessionStorage.removeItem(REVIEW_KEY); } catch { /* Status is optional; never affect stored records. */ }
  }
  function reportResult(result:SafeSyncResult) { setConflictCount(result.conflicts.length);setWaiting(!!result.deferred);setPending(result.settled===false && !result.deferred && !result.conflicts.length);setError(''); }
  useEffect(()=>{
    const refresh=()=>setLastSuccessfulAt(readLastSuccessfulSync(userId));
    const onStorage=(event:StorageEvent)=>{if(event.key===SYNC_STATUS_KEY || event.key===null)refresh();};
    const onConnection=()=>setOnline(navigator.onLine);
    refresh();window.addEventListener(SYNC_STATUS_EVENT,refresh);window.addEventListener('storage',onStorage);
    window.addEventListener('online',onConnection);window.addEventListener('offline',onConnection);
    return ()=>{window.removeEventListener(SYNC_STATUS_EVENT,refresh);window.removeEventListener('storage',onStorage);window.removeEventListener('online',onConnection);window.removeEventListener('offline',onConnection);};
  },[userId]);
  useEffect(()=>{
    if(!userId) { setChecking(false);setRunning(false);setWaiting(false);setPending(false);setError('');return; }
    let active=true;
    function canApply() {
      if(!active || !safe.current) return false;
      if(document.querySelector('[role="dialog"],dialog[open],.popup-backdrop,.voice-backdrop,.calendar-backdrop,.assistant-backdrop')) return false;
      const focused=document.activeElement;
      return !(focused instanceof HTMLElement && (focused.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(focused.tagName)));
    }
    const scheduler=createAutoSyncScheduler({
      // First manual sync establishes the owner and consent to upload local data.
      canStart:()=>{
        if(!active || !navigator.onLine)return false;
        const owner=syncOwner();
        if(owner && owner!==userId){setError('На устройстве данные другого аккаунта. Автоматическая синхронизация остановлена.');return false;}
        return owner===userId;
      },
      run:async()=>{
        if(!active)return;
        setChecking(true);
        const controller=new AbortController();const timeout=window.setTimeout(()=>controller.abort(),20000);
        let frozen=false;
        try {
          // Uploads and conflict checks never modify local data and may run while
          // an editor is open. Incoming changes wait until the home screen is idle.
          const preview=await syncBrowser(userId,controller.signal,true);
          if(active)reportResult(preview);
          if(!active || !preview.deferred || !scheduler.isIdle() || !canApply()) return;
          // Freeze the UI before any download, so an editor cannot be opened
          // between the compare-and-write and the subsequent application reload.
          flushSync(()=>setRunning(true)); frozen=true;
          const result=await syncBrowser(userId,controller.signal);
          if(active)reportResult(result);
        } catch (reason) {
          const detail=reason instanceof Error && reason.name!=='AbortError' && reason.name!=='TimeoutError' ? reason.message : '';
          if(active)setError(detail ? `Синхронизация: ${detail}` : 'Не удалось синхронизировать. Приложение повторит попытку.');
        } finally {
          clearTimeout(timeout);
          const changed=frozen && consumeLocalSyncChanges();
          if(changed) { window.location.reload(); return; }
          if(active){setChecking(false);if(frozen)setRunning(false);}
        }
      },
    });
    const activity=()=>scheduler.activity();
    const events=['pointerdown','keydown','touchstart','wheel','input'] as const;
    for(const event of events)document.addEventListener(event,activity,{passive:true});
    document.addEventListener('visibilitychange',activity);
    return ()=>{active=false;scheduler.stop();for(const event of events)document.removeEventListener(event,activity);document.removeEventListener('visibilitychange',activity);};
  },[userId]);
  return {running,checking,waiting,pending,online,lastSuccessfulAt,conflictCount,error,setConflictCount,reportResult};
}
